import React, { useState } from "react";
import { Icon } from "../UIElements/Icon";
import Modal from "../UIElements/Modal";
import {
  useWalletTransactionsHistory,
  parseWalletEvent,
} from "../../../hooks/useWalletTransactionsHistory";
import { useGlobalUtils } from "../../../lib/globalUtils";

const WalletTransactionHistoryModal = ({
  isOpen,
  onClose,
  wallet,
  userId,
  users = [],
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const { parsePrice, parseDate } = useGlobalUtils();

  const {
    data: responseData,
    isLoading,
    error,
    isError,
    refetch,
  } = useWalletTransactionsHistory(userId, wallet.id, {
    page: currentPage,
    pageSize,
    enabled: isOpen, // Only fetch when modal is open
  });

  const transactions = responseData?.transactions || [];
  const pagination = responseData?.pagination || {};

  const getWalletDisplayName = (type) => {
    switch (type) {
      case "cash":
        return "Cash Wallet";
      case "credit_point":
        return "Credit Points";
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const getWalletIcon = (type) => {
    switch (type) {
      case "cash":
        return "DollarSign";
      case "credit_point":
        return "Star";
      default:
        return "Wallet";
    }
  };

  const getWalletColor = (type) => {
    switch (type) {
      case "cash":
        return "text-green-400";
      case "credit_point":
        return "text-yellow-400";
      default:
        return "text-blue-400";
    }
  };

  const formatAmount = (amount, walletType) => {
    if (walletType === "credit_point") {
      return `${Math.abs(amount)} pts`;
    }
    return parsePrice(Math.abs(amount));
  };

  const getAmountColor = (amount) => {
    return Math.sign(amount) === -1 ? "text-red-400" : "text-green-400";
  };

  const getAmountIcon = (amount) => {
    return Math.sign(amount) === -1 ? "ArrowDown" : "ArrowUp";
  };

  // Reset page when modal opens and refetch data
  React.useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      // Refetch data when modal opens to get latest transactions
      refetch();
    }
  }, [isOpen, refetch]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRefresh = () => {
    refetch();
  };

  // Pagination helpers
  const totalPages = pagination.total_pages || 1;
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;
  const showPagination = totalPages > 1;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Transaction History - ${getWalletDisplayName(wallet?.type)}`}
      subtitle={`View all transactions for this wallet`}
      maxWidth="max-w-4xl"
    >
      <div className="space-y-4">
        {/* Wallet Info Header */}
        <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-gray-700 p-3">
              <Icon
                name={getWalletIcon(wallet?.type)}
                className={`h-6 w-6 ${getWalletColor(wallet?.type)}`}
              />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-white">
                {getWalletDisplayName(wallet?.type)}
              </h4>
              <p className="text-sm text-gray-400">
                Current Balance:{" "}
                {wallet?.type === "credit_point"
                  ? `${wallet.balance} pts`
                  : parsePrice(wallet?.balance)}
              </p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="flex items-center space-x-2 rounded-lg bg-gray-700 px-3 py-2 text-sm text-white transition-colors hover:bg-gray-600 disabled:opacity-50"
            >
              <Icon
                name="RefreshCw"
                className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
              />
              <span>{isLoading ? "Refreshing..." : "Refresh"}</span>
            </button>

            {transactions.length > 0 && (
              <span className="text-sm text-gray-400">
                Page {currentPage} of {totalPages} •{" "}
                {pagination.total || transactions.length} total transactions
              </span>
            )}
          </div>

          {/* Auto-refresh indicator */}
          {isLoading && (
            <div className="flex items-center space-x-2 text-sm text-blue-400">
              <Icon name="Loader2" className="h-4 w-4 animate-spin" />
              <span>Updating...</span>
            </div>
          )}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-3">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="rounded-lg bg-gray-800 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="h-8 w-8 rounded-full bg-gray-700"></div>
                      <div className="space-y-2">
                        <div className="h-3 w-32 rounded bg-gray-700"></div>
                        <div className="h-2 w-24 rounded bg-gray-700"></div>
                      </div>
                    </div>
                    <div className="h-4 w-16 rounded bg-gray-700"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div className="py-8 text-center">
            <Icon
              name="AlertCircle"
              className="mx-auto mb-3 h-8 w-8 text-red-400"
            />
            <h3 className="mb-1 text-sm font-medium text-white">
              Error loading transactions
            </h3>
            <p className="mb-4 text-xs text-gray-400">
              {error?.message || "Failed to load transaction history"}
            </p>
            <button
              onClick={handleRefresh}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!isLoading &&
          !isError &&
          (!transactions || transactions.length === 0) && (
            <div className="py-8 text-center">
              <Icon
                name="History"
                className="mx-auto mb-3 h-8 w-8 text-gray-400"
              />
              <h3 className="mb-1 text-sm font-medium text-white">
                No transactions found
              </h3>
              <p className="text-xs text-gray-400">
                This wallet doesn&apos;t have any transaction history yet.
              </p>
            </div>
          )}

        {/* Transactions List */}
        {!isLoading && !isError && transactions && transactions.length > 0 && (
          <div className="space-y-3">
            {transactions.map((transaction) => {
              const parsedEvent = parseWalletEvent(transaction, users);

              return (
                <div
                  key={transaction.id}
                  className="rounded-lg border border-gray-700 bg-gray-800 p-3 transition-colors hover:border-gray-600 sm:p-4"
                >
                  <div className="flex flex-col space-y-3 sm:flex-row sm:items-start sm:justify-between sm:space-y-0">
                    {/* Left side - Event info */}
                    <div className="flex min-w-0 flex-1 items-start space-x-3">
                      <div
                        className={`flex-shrink-0 rounded-lg bg-gray-700 p-2`}
                      >
                        <Icon
                          name={parsedEvent.eventIcon}
                          className={`h-4 w-4 ${parsedEvent.eventColor}`}
                        />
                      </div>

                      <div className="min-w-0 flex-1">
                        {/* Event description */}
                        <div className="mb-2 flex items-center space-x-2">
                          <p className="text-sm font-medium break-words text-white">
                            {parsedEvent.eventLog}
                          </p>
                        </div>

                        {/* Description */}
                        {parsedEvent.description && (
                          <p className="mb-2 text-sm break-words text-gray-400">
                            {parsedEvent.description}
                          </p>
                        )}

                        {/* Order information */}
                        {parsedEvent.orderId && (
                          <p className="mb-2 text-sm text-blue-400">
                            Order #{parsedEvent.orderId}
                          </p>
                        )}

                        {/* Transaction code */}
                        <div className="flex flex-col space-y-1">
                          <span className="text-xs text-gray-500">
                            Transaction Code:
                          </span>
                          <code className="w-fit overflow-hidden rounded bg-gray-900 px-2 py-1 font-mono text-xs break-all text-gray-400">
                            {transaction.code}
                          </code>
                        </div>
                      </div>
                    </div>

                    {/* Right side - Amount and date */}
                    <div className="flex flex-row items-center justify-between space-x-3 sm:ml-4 sm:flex-shrink-0 sm:flex-col sm:items-end sm:justify-start sm:space-y-2 sm:space-x-0">
                      {/* Amount */}
                      <div
                        className={`flex items-center space-x-2 ${getAmountColor(transaction.amount)}`}
                      >
                        <Icon
                          name={getAmountIcon(transaction.amount)}
                          className="h-4 w-4"
                        />
                        <span className="text-base font-semibold sm:text-lg">
                          {formatAmount(transaction.amount, wallet.type)}
                        </span>
                      </div>

                      {/* Date */}
                      <span className="text-xs text-gray-400 sm:text-sm">
                        {parseDate(transaction.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && !isError && showPagination && (
          <div className="flex flex-col items-center justify-between space-y-3 border-t border-gray-700 pt-4 sm:flex-row sm:space-y-0">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!hasPrevPage}
                className="flex items-center space-x-1 rounded-lg bg-gray-700 px-3 py-2 text-sm text-white hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Icon name="ChevronLeft" className="h-4 w-4" />
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">Prev</span>
              </button>

              <span className="px-3 py-2 text-sm text-gray-400">
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!hasNextPage}
                className="flex items-center space-x-1 rounded-lg bg-gray-700 px-3 py-2 text-sm text-white hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="hidden sm:inline">Next</span>
                <span className="sm:hidden">Next</span>
                <Icon name="ChevronRight" className="h-4 w-4" />
              </button>
            </div>

            <div className="text-center text-xs text-gray-400 sm:text-right sm:text-sm">
              {transactions.length} transactions on this page
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default WalletTransactionHistoryModal;
